import {
  buildTaylorSeriesCode,
  normalizeResult,
  parseNodeToSympy,
  parseSympyResult,
  semanticEquals,
} from "@corca/sympy";

import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isRelation, isSign } from "@/models/utils/semanticChecks";
import { createLogger } from "@/utils/logger";
import { trySympy } from "@/utils/sympyErrors";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";
import type { SemanticExpr, TaylorSeriesOptions } from "@corca/sympy";

const logger = createLogger("TaylorSeriesAction");

/** Where the last term of an expression starts: a leading sign, or the relation before it. */
function isTermBreaker(node: NodeModel): boolean {
  return isSign(node) || isRelation(node);
}

function extractLastTerm(nodes: readonly NodeModel[]): NodeModel[] {
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    if (isTermBreaker(nodes[i])) {
      if (i === 0) return [...nodes];
      return nodes.slice(i + 1);
    }
  }
  return [...nodes];
}

export type TaylorSeriesMode = "selection" | "cursor";

export type TaylorSeriesSelection =
  | {
      type: "ready";
      mode: TaylorSeriesMode;
      inlineNodeJson: NodeJson;
      expr: string;
      semantic: SemanticExpr;
      variables: Map<string, string>;
      symbolNodes: Map<string, NodeJson>;
      variableNames: string[];
      sourceNodes: NodeModel[];
      hasDecimal: boolean;
    }
  | { type: "unavailable" };

export function normalizeTaylorSeriesCenter(center: number): number | null {
  return Number.isFinite(center) ? center : null;
}

export function normalizeTaylorSeriesOrder(order: number): number | null {
  return Number.isInteger(order) && order > 0 ? order : null;
}

function resolveSourceNodes(
  doc: DocumentModel,
): { mode: TaylorSeriesMode; nodes: NodeModel[] } | null {
  const {
    selection: { isContinuousSelection, selectedNodes },
    cursor,
  } = doc;

  if (isContinuousSelection && selectedNodes.length > 0) {
    return { mode: "selection", nodes: [...selectedNodes] };
  }

  if (cursor.isActive) {
    const inlineNode = cursor.node;
    const { index } = cursor;
    if (
      inlineNode?.is(InlineNodeModel) &&
      inlineNode.parent?.is(WrapperNodeModel) &&
      index !== undefined
    ) {
      const allNodes = inlineNode.nodes;
      const atTermBoundary = index === allNodes.length || isTermBreaker(allNodes[index]);
      if (atTermBoundary) {
        const term = extractLastTerm(allNodes.slice(0, index));
        if (term.length > 0) {
          return { mode: "cursor", nodes: term };
        }
      }
    }
  }

  return null;
}

export function getTaylorSeriesSelection(doc: DocumentModel): TaylorSeriesSelection {
  const source = resolveSourceNodes(doc);

  if (!source) {
    return { type: "unavailable" };
  }

  if (!pyodideManager.isInitialized || pyodideManager.currentStatus === "starting") {
    return { type: "unavailable" };
  }

  const inlineNodeJson: NodeJson = {
    type: "inline",
    props: {},
    children: source.nodes.map((n) => n.toJSON()),
  };

  const parsed = trySympy(() => parseNodeToSympy(inlineNodeJson));
  if ("error" in parsed) return { type: "unavailable" };

  if (!parsed.expr) {
    return { type: "unavailable" };
  }

  parsed.warnings.forEach((warning) => logger.warn(warning));

  const variableNames = [...parsed.variables.keys()];

  if (variableNames.length === 0) {
    return { type: "unavailable" };
  }

  return {
    type: "ready",
    mode: source.mode,
    inlineNodeJson,
    expr: parsed.expr,
    semantic: parsed.semantic,
    variables: parsed.variables,
    symbolNodes: parsed.symbolNodes,
    variableNames,
    sourceNodes: source.nodes,
    hasDecimal: parsed.hasDecimal,
  };
}

export async function getTaylorSeriesResultJson(
  selection: Extract<TaylorSeriesSelection, { type: "ready" }>,
  options: TaylorSeriesOptions,
): Promise<{ nodes: NodeJson[]; equals: boolean } | null> {
  const center = normalizeTaylorSeriesCenter(options.center);
  const order = normalizeTaylorSeriesOrder(options.order);

  if (center === null || order === null) return null;

  const buildResult = trySympy(() =>
    buildTaylorSeriesCode(selection.expr, selection.variables, {
      ...options,
      center,
      order,
    }),
  );
  if ("error" in buildResult) {
    logger.warn("Build code error", buildResult.error);
    return null;
  }

  const res = await pyodideManager.executeSympyCode(buildResult.expr);

  const { result } = res;
  if (!result) return null;

  const parsedResult = trySympy(() =>
    parseSympyResult(result, undefined, selection.symbolNodes, selection.hasDecimal),
  );
  if ("error" in parsedResult) {
    logger.warn("Parse result error", parsedResult.error);
    return null;
  }
  parsedResult.warnings.forEach((warning) => logger.warn(warning));

  return {
    nodes: parsedResult.nodes,
    equals: semanticEquals(selection.semantic, normalizeResult(result)),
  };
}

export async function runTaylorSeriesAction(
  doc: DocumentModel,
  selection: Extract<TaylorSeriesSelection, { type: "ready" }>,
  options: TaylorSeriesOptions,
) {
  try {
    const result = await getTaylorSeriesResultJson(selection, options);
    if (!result || result.nodes.length === 0) return;

    const targetNodes = selection.sourceNodes;
    const firstTarget = targetNodes[0];
    const targetParent = firstTarget?.parent;
    if (!targetParent) return;

    doc.transact(() => {
      if (!firstTarget.parent || firstTarget.parent !== targetParent) return;

      const newNodes = result.nodes
        .map((json) => nodeFromJson(doc, json, { resetIds: true }))
        .filter((n) => n !== undefined);

      newNodes.forEach((node) => {
        if (node) firstTarget.insertSiblingBefore(node);
      });

      targetNodes.forEach((node) => {
        doc.removeNode(node);
      });

      doc.selection.resetSelection();
    }, "TaylorSeriesInsert");
  } catch (e) {
    console.error("TaylorSeriesAction: Error", e);
  }
}
